/**
 * InviteByEmail — the shared invite-a-new-user-to-the-workspace form.
 *
 * Renders an email + role form. A single address POSTs to
 * `/api/members/invites` and shows a copyable invite link; multiple addresses
 * (comma/space/newline separated — a pasted roster) POST to
 * `/api/members/invites/bulk` and show a per-address outcome list. Used by:
 *   - TeamView's "Invite" modal
 *   - InviteReaderDialog's "Invite by email" tab (desk settings)
 *
 * The component owns the invite POST and copy-link interaction; the parent
 * owns any surrounding Dialog/Tabs chrome and the `onError` surface.
 *
 * Non-admins see an admin-only message instead of the form — the backend
 * `requireWorkspaceRole("admin")` gate is mirrored here so the form never
 * appears for users who can't use it.
 *
 * @example
 * <InviteByEmail isAdmin onError={setError} onInvited={(inv) => setInvites([inv, ...prev])} />
 */
import { useCallback, useState } from "react";
import { Check, Copy } from "lucide-react";
import {
  WORKSPACE_ROLE_PERMISSIONS,
  type BulkInviteResult,
  type WorkspaceInvite,
  type WorkspaceMembershipRole,
} from "@yagni/shared";
import { Button, Input, FormField } from "./index.js";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./index.js";
import { readJsonError } from "../lib/readJsonError.js";
import { useCopyToClipboard } from "../hooks/useCopyToClipboard.js";

export const INVITE_ROLE_OPTIONS: { value: WorkspaceMembershipRole; label: string; description: string }[] = [
  { value: "member", label: WORKSPACE_ROLE_PERMISSIONS.member.label, description: WORKSPACE_ROLE_PERMISSIONS.member.summary },
  { value: "admin", label: WORKSPACE_ROLE_PERMISSIONS.admin.label, description: WORKSPACE_ROLE_PERMISSIONS.admin.summary },
  { value: "owner", label: WORKSPACE_ROLE_PERMISSIONS.owner.label, description: WORKSPACE_ROLE_PERMISSIONS.owner.summary },
];

export function parseInviteEmails(raw: string): string[] {
  const seen = new Set<string>();
  const emails: string[] = [];
  // Mail clients copy rosters as `Name <email>` — unwrap the address and let
  // the name tokens fall through the @-filter below.
  const unwrapped = raw.replace(/<([^<>\s]+@[^<>\s]+)>/g, (_match, addr: string) => ` ${addr} `);
  for (const part of unwrapped.split(/[\s,;<>]+/)) {
    const email = part.trim();
    if (!email || !email.includes("@")) continue;
    const key = email.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    emails.push(email);
  }
  return emails;
}

interface Props {
  /** Whether the current user is a workspace admin/owner. */
  isAdmin: boolean;
  /** Surface a server error in the parent's error area. */
  onError: (msg: string) => void;
  /**
   * Called after a successful invite creation, with the new invite. Lets the
   * parent (e.g. TeamView) update its pending-invites list. Fires once per
   * created invite on the bulk path.
   */
  onInvited?: (invite: WorkspaceInvite) => void;
  /**
   * Size variant for inputs/buttons. `"sm"` matches the desk-settings dialog;
   * the default matches TeamView's modal.
   */
  inputSize?: "sm" | "md";
}

const BULK_STATUS_LABELS: Record<BulkInviteResult["status"], string> = {
  invited: "Invited",
  already_member: "Already a member",
  failed: "Failed — try again",
};

export function InviteByEmail({
  isAdmin,
  onError,
  onInvited,
  inputSize = "md",
}: Props) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<WorkspaceMembershipRole>("member");
  const [pending, setPending] = useState(false);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [bulkResults, setBulkResults] = useState<BulkInviteResult[] | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [copied, copy] = useCopyToClipboard();

  const handleInvite = useCallback(async () => {
    const emails = parseInviteEmails(email);
    if (emails.length === 0) return;
    setPending(true);
    setLocalError(null);
    setInviteLink(null);
    setBulkResults(null);
    try {
      if (emails.length === 1) {
        const res = await fetch("/api/members/invites", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ email: emails[0], role }),
        });
        if (!res.ok) {
          const msg = await readJsonError(res);
          setLocalError(msg);
          onError(msg);
          return;
        }
        const data = (await res.json()) as { invite: WorkspaceInvite };
        setInviteLink(`${window.location.origin}/invite/${data.invite.token ?? ""}`);
        setExpiresAt(data.invite.expiresAt);
        onInvited?.(data.invite);
      } else {
        const res = await fetch("/api/members/invites/bulk", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ emails, role }),
        });
        if (!res.ok) {
          const msg = await readJsonError(res);
          setLocalError(msg);
          onError(msg);
          return;
        }
        const data = (await res.json()) as { results: BulkInviteResult[] };
        setBulkResults(data.results);
        setExpiresAt(data.results.find((r) => r.invite)?.invite?.expiresAt ?? null);
        for (const result of data.results) {
          if (result.invite) onInvited?.(result.invite);
        }
      }
    } catch {
      setLocalError("Failed to create invite");
      onError("Failed to create invite");
    } finally {
      setPending(false);
    }
  }, [email, role, onError, onInvited]);

  const reset = useCallback(() => {
    setEmail("");
    setRole("member");
    setLocalError(null);
    setInviteLink(null);
    setBulkResults(null);
    setExpiresAt(null);
  }, []);

  if (!isAdmin) {
    return (
      <div className="py-md text-sm text-text-secondary">
        <p>
          Only workspace admins can invite new users. Ask an admin to invite
          them, then add them as a reader from the "From team" tab once they've
          joined.
        </p>
      </div>
    );
  }

  const expiryNote = expiresAt
    ? `Invites expire on ${new Date(expiresAt).toLocaleDateString()}.`
    : "Invites expire after a few days.";

  if (inviteLink) {
    return (
      <div className="flex flex-col gap-sm">
        <p className="text-sm text-text-secondary">
          Share this link with <strong>{parseInviteEmails(email)[0]}</strong>:
        </p>
        <div className="flex items-center gap-xs">
          <Input
            type="text"
            readOnly
            value={inviteLink}
            className="flex-1 bg-[var(--bg-code)] font-mono text-text-secondary select-all"
            inputSize={inputSize}
          />
          <Button
            variant="secondary"
            size={inputSize === "sm" ? "sm" : "md"}
            onClick={() => void copy(inviteLink)}
          >
            {copied ? (
              <Check width={inputSize === "sm" ? 14 : 16} className="text-success" />
            ) : (
              <Copy width={inputSize === "sm" ? 14 : 16} />
            )}
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>
        <p className="text-xs text-text-tertiary">
          {expiryNote} Once they accept and join the workspace, add them as a
          reader from the "From team" tab.
        </p>
        <Button variant="ghost" size={inputSize === "sm" ? "sm" : "md"} onClick={reset}>
          Invite another
        </Button>
      </div>
    );
  }

  if (bulkResults) {
    const invitedCount = bulkResults.filter((r) => r.status === "invited").length;
    return (
      <div className="flex flex-col gap-sm">
        <p className="text-sm text-text-secondary">
          Invited <strong>{invitedCount}</strong> of {bulkResults.length}{" "}
          {bulkResults.length === 1 ? "address" : "addresses"}. Invite emails
          are on their way; each row's link works immediately.
        </p>
        <ul className="flex flex-col gap-xs max-h-64 overflow-y-auto">
          {bulkResults.map((result) => (
            <li key={result.email} className="flex items-center gap-xs text-sm">
              <span className="flex-1 min-w-0 truncate text-text-primary">{result.email}</span>
              <span
                className={
                  result.status === "failed"
                    ? "text-xs text-error"
                    : "text-xs text-text-tertiary"
                }
              >
                {BULK_STATUS_LABELS[result.status]}
              </span>
              {result.invite?.token && (
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Copy invite link for ${result.email}`}
                  onClick={() =>
                    void copy(`${window.location.origin}/invite/${result.invite!.token}`)
                  }
                >
                  <Copy width={14} />
                </Button>
              )}
            </li>
          ))}
        </ul>
        <p className="text-xs text-text-tertiary">{expiryNote}</p>
        <Button variant="ghost" size={inputSize === "sm" ? "sm" : "md"} onClick={reset}>
          Invite more
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-sm">
      <FormField label="Email address" htmlFor="invite-email">
        <Input
          id="invite-email"
          type="text"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="colleague@company.com, teammate@company.com"
          inputSize={inputSize}
        />
      </FormField>
      <p className="text-xs text-text-tertiary -mt-1">
        Paste multiple addresses separated by commas or spaces to invite a
        whole team at once.
      </p>

      <FormField label="Workspace role" htmlFor="invite-role">
        <Select value={role} onValueChange={(v) => setRole(v as WorkspaceMembershipRole)}>
          <SelectTrigger id="invite-role" className="h-9">
            {/* Radix echoes the selected item's children into the trigger, so
                pin the trigger to the short label; the description lives in
                the dropdown only. */}
            <SelectValue>
              {INVITE_ROLE_OPTIONS.find((opt) => opt.value === role)?.label}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {INVITE_ROLE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value} textValue={opt.label}>
                <span className="flex flex-col">
                  <span>{opt.label}</span>
                  <span className="text-xs text-[var(--text-tertiary)] font-normal">
                    {opt.description}
                  </span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>

      {localError && (
        <p className="text-sm text-error" role="alert">
          {localError}
        </p>
      )}

      <Button
        className="w-full"
        size={inputSize === "sm" ? "sm" : "md"}
        loading={pending}
        disabled={parseInviteEmails(email).length === 0}
        onClick={() => void handleInvite()}
      >
        {pending
          ? "Creating invites…"
          : parseInviteEmails(email).length > 1
            ? `Create ${parseInviteEmails(email).length} invites`
            : "Create invite link"}
      </Button>
    </div>
  );
}
